import { Check, ChevronsUpDown, Clock, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { DateQuickSelect } from '@/components/ui/date-quick-select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toast'
import { useIssues, type Issue } from '@/hooks'
import { useCreateTimelog } from '@/hooks'
import { cn } from '@/lib/utils'

export interface QuickTimelogPopoverProps {
  /** 预填的任务 ID */
  issueId?: string
  /** 预填的任务名称（用于显示） */
  issueName?: string
  /** 预填的项目 ID */
  projectId?: string
  /** 触发器元素 */
  trigger: React.ReactNode
  /** 成功回调 */
  onSuccess?: () => void
  /** 自定义类名 */
  className?: string
  /** 受控模式：是否打开 */
  open?: boolean
  /** 受控模式：打开状态变化回调 */
  onOpenChange?: (open: boolean) => void
}

/**
 * 任务搜索选择器组件
 */
function IssueSearchSelector({
  value,
  onChange,
  projectId,
  error,
}: {
  value: { id: string; code: string; title: string } | null
  onChange: (issue: { id: string; code: string; title: string } | null) => void
  projectId?: string
  error?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // 搜索任务
  const { data: issues = [], isLoading } = useIssues({
    projectId,
    search: search || undefined,
    pageSize: 10,
  })

  // 打开时聚焦搜索框
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus()
    }
  }, [open])

  const handleSelect = (issue: Issue) => {
    onChange({ id: issue.id, code: issue.code, title: issue.title })
    setOpen(false)
    setSearch('')
  }

  return (
    <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between font-normal',
              !value && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            {value ? (
              <span className="truncate">
                <span className="text-muted-foreground">{value.code}</span>{' '}
                {value.title}
              </span>
            ) : (
              '选择任务...'
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索任务..."
              className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            {isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                搜索中...
              </div>
            ) : issues.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {search ? '未找到任务' : '输入关键词搜索'}
              </div>
            ) : (
              issues.map((issue) => (
                <div
                  key={issue.id}
                  onClick={() => handleSelect(issue)}
                  className={cn(
                    'flex cursor-pointer items-center px-3 py-2 text-sm hover:bg-accent',
                    value?.id === issue.id && 'bg-accent'
                  )}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value?.id === issue.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="mr-2 text-muted-foreground">{issue.code}</span>
                  <span className="truncate">{issue.title}</span>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  )
}

/**
 * 快捷工时记录 Popover 组件
 * 支持在任务卡片上快速记录工时
 */
export function QuickTimelogPopover({
  issueId,
  issueName,
  projectId,
  trigger,
  onSuccess,
  className,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: QuickTimelogPopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  
  // 支持受控和非受控模式
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled
    ? (value: boolean) => controlledOnOpenChange?.(value)
    : setInternalOpen
  const [selectedIssue, setSelectedIssue] = useState<{
    id: string
    code: string
    title: string
  } | null>(issueId && issueName ? { id: issueId, code: '', title: issueName } : null)
  const [formData, setFormData] = useState({
    hours: 1,
    minutes: 0,
    logDate: new Date().toISOString().split('T')[0],
    description: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const createMutation = useCreateTimelog()
  const toast = useToast()

  // 是否需要显示任务选择器（没有预填任务时显示）
  const showIssueSelector = !issueId

  const resetForm = () => {
    setFormData({
      hours: 1,
      minutes: 0,
      logDate: new Date().toISOString().split('T')[0],
      description: '',
    })
    if (!issueId) {
      setSelectedIssue(null)
    }
    setErrors({})
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}

    const currentIssueId = issueId || selectedIssue?.id
    if (!currentIssueId) {
      newErrors.issueId = '请选择任务'
    }

    if (!formData.logDate) {
      newErrors.logDate = '请选择日期'
    } else {
      const logDate = new Date(formData.logDate)
      const today = new Date()
      today.setHours(23, 59, 59, 999)
      if (logDate > today) {
        newErrors.logDate = '日期不能是未来'
      }
    }

    const totalMinutes = formData.hours * 60 + formData.minutes
    if (totalMinutes <= 0) {
      newErrors.time = '工时必须大于0'
    } else if (totalMinutes > 1440) {
      newErrors.time = '工时不能超过24小时'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    const currentIssueId = issueId || selectedIssue?.id
    if (!currentIssueId) {
      return
    }

    const totalMinutes = formData.hours * 60 + formData.minutes

    createMutation.mutate(
      {
        issueId: currentIssueId,
        logDate: formData.logDate,
        minutes: totalMinutes,
        description: formData.description || undefined,
      },
      {
        onSuccess: () => {
          const taskName = issueName || selectedIssue?.title || '任务'
          toast.success(
            '工时已记录',
            `已为 ${taskName} 记录 ${formData.hours}h ${formData.minutes}m`
          )
          resetForm()
          setOpen(false)
          onSuccess?.()
        },
        onError: (error) => {
          toast.error('记录失败', error.message || '请稍后重试')
        },
      }
    )
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      resetForm()
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className={cn('w-80', className)} align="start">
        <form onSubmit={handleSubmit}>
          {/* 头部 */}
          <div className="mb-4 flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">快速记录工时</span>
          </div>

          {/* 任务信息或选择器 */}
          {showIssueSelector ? (
            <div className="mb-4">
              <Label className="mb-2 block">
                任务 <span className="text-destructive">*</span>
              </Label>
              <IssueSearchSelector
                value={selectedIssue}
                onChange={setSelectedIssue}
                projectId={projectId}
                error={errors.issueId}
              />
            </div>
          ) : issueName ? (
            <div className="mb-4 rounded-md bg-muted p-2 text-sm">
              <span className="text-muted-foreground">任务：</span>
              <span className="font-medium">{issueName}</span>
            </div>
          ) : null}

          <div className="space-y-4">
            {/* 工时输入 */}
            <div>
              <Label className="mb-2 block">
                工时 <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="24"
                  value={formData.hours}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      hours: parseInt(e.target.value) || 0,
                    }))
                  }
                  className={cn('w-16', errors.time && 'border-destructive')}
                />
                <span className="text-sm text-muted-foreground">小时</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={formData.minutes}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      minutes: parseInt(e.target.value) || 0,
                    }))
                  }
                  className={cn('w-16', errors.time && 'border-destructive')}
                />
                <span className="text-sm text-muted-foreground">分钟</span>
              </div>
              {errors.time && (
                <p className="mt-1 text-sm text-destructive">{errors.time}</p>
              )}
            </div>

            {/* 日期选择 */}
            <div>
              <Label className="mb-2 block">
                日期 <span className="text-destructive">*</span>
              </Label>
              <Input
                type="date"
                value={formData.logDate}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, logDate: e.target.value }))
                }
                max={new Date().toISOString().split('T')[0]}
                className={cn(errors.logDate && 'border-destructive')}
              />
              {/* 日期快捷选项 (Requirements: 11.6) - 工时记录只能选择今天 */}
              <DateQuickSelect
                value={formData.logDate}
                onChange={(date) =>
                  setFormData((prev) => ({ ...prev, logDate: date }))
                }
                className="mt-2"
                options={{
                  includeToday: true,
                  includeTomorrow: false,
                  includeNextMonday: false,
                }}
              />
              {errors.logDate && (
                <p className="mt-1 text-sm text-destructive">{errors.logDate}</p>
              )}
            </div>

            {/* 描述 */}
            <div>
              <Label className="mb-2 block">描述</Label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="工作内容..."
                rows={2}
                maxLength={500}
                className="resize-none"
              />
            </div>
          </div>

          {/* 按钮 */}
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
            >
              取消
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? '记录中...' : '记录'}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}
